"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, CircleCheck as CheckCircle2, Building2, House as Home, Factory, ClipboardCheck, FileCheck2, HardHat, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
export default function SafetyAccess() {
  const shouldReduceMotion = useReducedMotion();
  const environments = [{
    title: "Business & technology parks",
    desc: "Planned access for commercial campuses and aerospace tech facilities, scheduled to minimise disruption to daily operations.",
    icon: Building2
  }, {
    title: "Industrial & commercial units",
    desc: "Heavy-duty loading bays, plant room access, and perimeter scaffolds designed for maintenance and refurbishments.",
    icon: Factory
  }, {
    title: "Residential streets",
    desc: "Neat, secure setups for domestic roofing, extensions, and chimney work with pavement license coordination where required.",
    icon: Home
  }];
  const safetySteps = [{
    step: "01",
    title: "Site assessment",
    desc: "We inspect ground conditions, public footpaths, overhead services, and building loads before preparing your design.",
    icon: HardHat
  }, {
    step: "02",
    title: "Safe erection & access",
    desc: "Erected by qualified scaffolders following NASC SG4 and TG20 guidance with full edge protection and ladder gates.",
    icon: ShieldCheck
  }, {
    step: "03",
    title: "Handover inspection",
    desc: "Every scaffold is formally inspected, tagged with Scafftag certification, and handed over with a signed compliance certificate.",
    icon: ClipboardCheck
  }];
  return <section id="farnboroughsafetyandaccess" data-nav="light" className="bg-background text-foreground py-16 sm:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <Badge variant="outline" className="mb-4 text-xs font-semibold uppercase tracking-wider">
            Farnborough GU14 &amp; Surrounds
          </Badge>
          <motion.h2 initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Safe access across Farnborough, built around your site.
          </motion.h2>
          <motion.p initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Centred around GU14 as the principal Farnborough district alongside nearby commercial corridors, every scaffold structure starts with a thorough site survey and concludes with a formal handover certificate.
          </motion.p>
        </div>

        {/* Access Environments in Farnborough */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {environments.map((item, i) => {
          const IconComponent = item.icon;
          return <motion.div key={item.title} data-index={i} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.1,
            ease: "easeOut"
          }}>
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200">
                  <CardHeader className="pb-3">
                    <div className="size-11 rounded-lg bg-muted flex items-center justify-center text-foreground mb-3">
                      <IconComponent className="size-5" />
                    </div>
                    <CardTitle className="text-xl font-bold text-card-foreground">
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm sm:text-base text-muted-foreground leading-normal">
                      {item.desc}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Safety & Compliance Section */}
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-10 lg:p-12 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Left Column: Safety Process */}
            <div className="lg:col-span-7">
              <div className="mb-8">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Our safety framework
                </span>
                <h3 className="text-2xl sm:text-3xl font-bold text-card-foreground mt-2">
                  Standardised protocols on every scaffold
                </h3>
                <p className="mt-2 text-sm sm:text-base text-muted-foreground">
                  We manage structural safety, pedestrian protection, and routine 7-day inspections to keep your site fully compliant.
                </p>
              </div>

              <div className="space-y-6">
                {safetySteps.map((step, idx) => {
                const StepIcon = step.icon;
                return <div key={step.step} data-index={idx} className="flex items-start gap-4 p-4 rounded-xl bg-background border border-border">
                      <div className="size-10 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shrink-0 font-semibold text-sm">
                        <StepIcon className="size-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-muted-foreground">STEP {step.step}</span>
                          <span className="font-semibold text-foreground text-base">{step.title}</span>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                          {step.desc}
                        </p>
                      </div>
                    </div>;
              })}
              </div>
            </div>

            {/* Right Column: Confirmed Accreditations & Trust */}
            <div className="lg:col-span-5 bg-background rounded-xl p-6 border border-border flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <ShieldCheck className="size-5 text-foreground" />
                  <h4 className="font-bold text-lg text-foreground">Accredited standards</h4>
                </div>
                <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
                  BMF Scaffolding operates under verified safety schemes, giving principal contractors and property owners complete peace of mind.
                </p>

                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-card border border-border">
                    <CheckCircle2 className="size-5 text-foreground shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-sm text-card-foreground">NASC Audited Contractor</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Strict operational audits, working directly to industry safety guidelines.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 rounded-lg bg-card border border-border">
                    <CheckCircle2 className="size-5 text-foreground shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-sm text-card-foreground">CHAS Accredited</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Demonstrated health and safety compliance across management and site operations.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 rounded-lg bg-card border border-border">
                    <FileCheck2 className="size-5 text-foreground shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-sm text-card-foreground">Handover Certificates &amp; Scafftags</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Immediate certification upon completion plus statutory 7-day reinspections.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Separator className="my-4" />
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button asChild className="w-full sm:w-auto">
                    <Link to="/ContactRequestQuotePage">
                      Request site survey
                      <ArrowRight className="size-4 ml-1" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="w-full sm:w-auto">
                    <Link to="/HealthSafetyAccreditationsPage">
                      View accreditations
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Nearby Areas & Regional Coverage */}
        <div className="mt-12 pt-8 border-t border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">Nearby areas we cover from GU14:</span>
            <div className="flex flex-wrap items-center gap-3">
              <Link to="#" className="font-medium text-foreground hover:text-primary transition-colors underline-offset-4 hover:underline">
                Fleet
              </Link>
              <span className="text-border" aria-hidden="true">•</span>
              <Link to="#" className="font-medium text-foreground hover:text-primary transition-colors underline-offset-4 hover:underline">
                Aldershot
              </Link>
              <span className="text-border" aria-hidden="true">•</span>
              <Link to="#" className="font-medium text-foreground hover:text-primary transition-colors underline-offset-4 hover:underline">
                Camberley
              </Link>
            </div>
          </div>
          <Link to="#" className="inline-flex items-center text-sm font-semibold text-primary hover:underline group">
            View all areas
            <ArrowRight className="size-4 ml-1 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      </div>
    </section>;
}